import React from 'react'
import { LightTooltip } from '../styled/CustomComponent'
import { Avatar, Box, Typography, useTheme, alpha } from '@mui/material'
import { font } from '../../styles/font';

export default function CreatedByAvatar({ data, size = "small" }) {
    const theme = useTheme();

    // Size configuration
    const sizeConfig = {
        small: {
            avatar: { width: 24, height: 24, fontSize: font.size.fs_6 },
            tooltip: { fontSize: font.size.fs_6 }
        },
        medium: {
            avatar: { width: 32, height: 32, fontSize: font.size.fs_5 },
            tooltip: { fontSize: font.size.fs_5 }
        },
        large: {
            avatar: { width: 40, height: 40, fontSize: font.size.fs_4 },
            tooltip: { fontSize: font.size.fs_4 }
        }
    };

    const config = sizeConfig[size] || sizeConfig.small;

    // Generate avatar background color based on name
    const getAvatarColor = (name) => {
        if (!name) return theme.palette.secondary.light;
        
        // Simple hash function to generate consistent colors
        let hash = 0;
        for (let i = 0; i < name.length; i++) {
            hash = name.charCodeAt(i) + ((hash << 5) - hash);
        }
        
        const hue = Math.abs(hash % 360);
        
        // Adjust saturation and lightness based on theme
        const saturation = theme.palette.mode === 'dark' ? 60 : 50;
        const lightness = theme.palette.mode === 'dark' ? 35 : 55;
        
        return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
    };

    // Generate text color based on background for contrast
    const getTextColor = (backgroundColor) => {
        // Parse HSL to get lightness
        const lightnessMatch = backgroundColor.match(/hsl\((\d+),\s*(\d+)%,\s*(\d+)%\)/);
        
        if (lightnessMatch) {
            const lightness = parseInt(lightnessMatch[3]);
            
            // For dark theme: use white text on darker backgrounds, black on lighter
            if (theme.palette.mode === 'dark') {
                return lightness > 50 ? theme.palette.common.black : theme.palette.common.black;
            } 
            // For light theme: use black text on lighter backgrounds, white on darker
            else {
                return lightness > 50 ? theme.palette.common.white : theme.palette.common.white;
            }
        }
        
        // Fallback based on theme mode
        return theme.palette.mode === 'dark' 
            ? theme.palette.common.white 
            : theme.palette.common.black;
    };

    const avatarBackground = getAvatarColor(data?.createdBy?.name);
    const avatarTextColor = getTextColor(avatarBackground);

    // Get border color based on theme
    const getBorderColor = () => {
        return theme.palette.mode === 'dark' 
            ? theme.palette.divider 
            : theme.palette.primary.light;
    };

    // Get hover effect based on theme
    const getHoverEffect = () => {
        return theme.palette.mode === 'dark'
            ? {
                transform: 'scale(1.1)',
                boxShadow: `0 0 0 2px ${theme.palette.primary.main}`,
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
            }
            : {
                transform: 'scale(1.1)',
                boxShadow: `0 0 0 2px ${theme.palette.primary.light}`,
                backgroundColor: alpha(theme.palette.primary.main, 1),
            };
    };

    return (
        <LightTooltip
            title={
                <Box className="flex flex-col gap-0.5 p-1" sx={{ minWidth: 180 }}>
                    <Box className="flex items-center gap-2">
                        <Avatar 
                            sx={{ 
                                width: 32, 
                                height: 32, 
                                fontSize: font.size.fs_5, 
                                fontWeight: "bold", 
                                background: avatarBackground, 
                                color: avatarTextColor,
                                border: `1px solid ${theme.palette.divider}`,
                                borderColor: getBorderColor(),
                            }}
                        >
                            {data?.createdBy?.name?.charAt(0)?.toUpperCase() || '?'}
                        </Avatar>
                        <Box className="flex flex-col">
                            <Typography 
                                sx={{ 
                                    fontSize: font.size.fs_5,
                                    fontWeight: 600,
                                    color: theme.palette.text.primary,
                                    lineHeight: 1.2
                                }}
                            >
                                {data?.createdBy?.name || 'Unknown User'}
                            </Typography>
                            <Typography 
                                sx={{ 
                                    fontSize: font.size.fs_6,
                                    color: theme.palette.text.secondary,
                                    lineHeight: 1.2
                                }}
                            >
                                {data?.createdBy?.email || 'No email'}
                            </Typography>
                        </Box>
                    </Box>
                    
                    {data?.createdBy?.role && (
                        <Box 
                            sx={{ 
                                backgroundColor: theme.palette.action.hover,
                                borderRadius: theme.shape.borderRadius,
                                padding: '4px 8px',
                                marginTop: '4px',
                                border: `1px solid ${theme.palette.divider}`,
                            }}
                        >
                            <Typography 
                                sx={{ 
                                    fontSize: font.size.fs_6,
                                    color: theme.palette.text.secondary,
                                    fontStyle: 'italic'
                                }}
                            >
                                {data.createdBy.role}
                            </Typography>
                        </Box>
                    )}
                </Box>
            }
            placement="top"
            arrow
            slotProps={{
                tooltip: {
                    sx: {
                        backgroundColor: theme.palette.mode === 'dark' 
                            ? theme.palette.background.paper 
                            : theme.palette.background.paper,
                        color: theme.palette.text.primary,
                        border: `1px solid ${theme.palette.divider}`,
                        boxShadow: theme.shadows[4],
                    }
                },
                arrow: {
                    sx: {
                        color: theme.palette.mode === 'dark' 
                            ? theme.palette.background.paper 
                            : theme.palette.background.paper,
                    }
                }
            }}
        >
            <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                <Avatar 
                    sx={{ 
                        width: config.avatar.width, 
                        height: config.avatar.height, 
                        fontSize: config.avatar.fontSize, 
                        fontWeight: "bold", 
                        background: avatarBackground, 
                        color: avatarTextColor,
                        border: `1px solid ${getBorderColor()}`,
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        '&:hover': getHoverEffect(),
                    }}
                >
                    {data?.createdBy?.name?.charAt(0)?.toUpperCase() || '?'}
                </Avatar>
                
                {/* Show name next to avatar for medium/large sizes */}
                {(size === 'medium' || size === 'large') && data?.createdBy?.name && (
                    <Typography 
                        sx={{ 
                            marginLeft: 1,
                            fontSize: config.tooltip.fontSize,
                            color: theme.palette.text.secondary,
                            fontWeight: 500,
                            maxWidth: 120,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                        }}
                    >
                        {data.createdBy.name}
                    </Typography>
                )}
            </Box>
        </LightTooltip>
    )
}